<template>
  <div v-if="loading" class="loading-slot">
    <RiftLoader overlay size="lg" :label="t('gallery.loading')" :note="t('gallery.loadingMessage')" />
  </div>

  <StateMessage
    v-else-if="error"
    tone="error"
    :title="t('gallery.errorTitle')"
    :message="t('gallery.errorMessage')"
    :retry-label="t('gallery.retry')"
    :on-retry="loadPhotos"
  />

  <StateMessage
    v-else-if="photos.length === 0"
    :title="t('gallery.emptyTitle')"
    :message="t('gallery.emptyMessage')"
  />

  <MediaGrid v-else min="16rem">
    <li v-for="(photo, index) in photos" :key="photo.id">
      <Figure
        zoomable
        :src="photo.image"
        :alt="titleOf(photo) || t('gallery.imageAlt', { n: index + 1 })"
        :caption="titleOf(photo)"
        :zoom-label="t('gallery.openImage', { title: titleOf(photo) || t('gallery.untitled') })"
        ratio="4 / 5"
        :eager="index < 4"
        @zoom="openAt(index)"
      />
    </li>
  </MediaGrid>

  <Lightbox
    :open="zoomedIndex !== null"
    :src="zoomed?.image || ''"
    :alt="titleOf(zoomed) || t('gallery.untitled')"
    :caption="describe(zoomed) || titleOf(zoomed) || ''"
    :label="t('a11y.imageViewer')"
    :close-label="t('a11y.closeViewer')"
    :index="zoomedIndex ?? 0"
    :total="photos.length"
    :prev-label="t('a11y.previousImage')"
    :next-label="t('a11y.nextImage')"
    @close="zoomedIndex = null"
    @prev="step(-1)"
    @next="step(1)"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RiftLoader from '@/components/loader/RiftLoader.vue';
import Figure from '@/components/public/Figure.vue';
import Lightbox from '@/components/public/Lightbox.vue';
import MediaGrid from '@/components/public/MediaGrid.vue';
import StateMessage from '@/components/public/StateMessage.vue';
import { GalleryGameApi } from '@/api/backendService.js';

const { t } = useI18n();

const photos = ref([]);
const loading = ref(true);
const error = ref(false);
const zoomedIndex = ref(null);

const zoomed = computed(() => (zoomedIndex.value === null ? null : photos.value[zoomedIndex.value]));

/*
 * Most gallery records carry an auto-generated name like "Art #1735150102.33".
 * Showing that as a caption is noise, so it is treated as no title at all —
 * the tile then falls back to a numbered alt and renders without a caption.
 */
/*
 * Gallery records carry machine values in their human fields: auto-generated
 * names like "Art #1735150102.33", and descriptions that are sometimes a bare
 * UUID. Rendering those as captions is worse than rendering nothing.
 */
const AUTO_NAME = /^art\s*#?\s*[\d.]+$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isMachineValue = (text) => !text || UUID.test(text) || AUTO_NAME.test(text) || !/\s/.test(text);

const titleOf = (art) => {
  const name = art?.name?.trim();
  return name && !isMachineValue(name) ? name : '';
};

/*
 * Descriptions are stored as raw generator prompts: they open with a tool name
 * and timestamp, and many were saved truncated mid-word. Strip the machine
 * prefix and end on the last complete sentence so the caption reads as prose.
 */
/* Non-greedy up to the first " - " separator, so the hyphens inside a date
 * like "DALL·E 2024-05-31 21.25.15 - " do not end the match early. */
const GENERATOR_PREFIX = /^\s*(dall[·.\-\s]?e|midjourney|stable\s*diffusion)\b.*?\s+-\s+/i;
const describe = (art) => {
  let text = art?.description?.trim();
  if (isMachineValue(text)) return '';
  text = text.replace(GENERATOR_PREFIX, '').trim();
  if (isMachineValue(text)) return '';
  const lastStop = Math.max(text.lastIndexOf('. '), text.lastIndexOf('! '), text.lastIndexOf('? '));
  if (lastStop > 60) text = text.slice(0, lastStop + 1);
  else if (text.length > 160) text = `${text.slice(0, 157).trimEnd()}…`;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

async function loadPhotos() {
  loading.value = true;
  error.value = false;
  try {
    const artObjects = (await GalleryGameApi.galleryWorldList()).data;
    photos.value = (artObjects || []).filter((art) => !!art.image);
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}

function openAt(index) {
  zoomedIndex.value = index;
}

function step(delta) {
  if (zoomedIndex.value === null || !photos.value.length) return;
  const n = photos.value.length;
  zoomedIndex.value = (zoomedIndex.value + delta + n) % n;
}

onMounted(loadPhotos);
</script>

<style scoped>
/* RiftLoader overlay is position:absolute; it needs a box to fill. */
.loading-slot {
  position: relative;
  min-height: 40dvh;
}
</style>
